/**
 * Getting started data: the hub's onboarding and templates modules, plus light probes of the
 * modules a step uses when they are installed (auth for sign-in, missions for starting a
 * mission, notify for push). A module the hub does not have answers 404, and the step says
 * what works without it. Demo mode (?mock=1) keeps everything in this tab.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import {
  BUILTIN_TEMPLATES,
  applyOnboardingUpdate,
  emptyOnboardingState,
  normalizeUrl,
  renderTemplate,
  templatePrompt,
} from '@orgus/protocol';
import type {
  HubReach,
  MissionTemplate,
  OnboardingModule,
  OnboardingResponse,
  OnboardingState,
  OnboardingUpdate,
  TemplateInput,
  TemplateStartRequest,
  TemplateStartResponse,
  TemplatesState,
} from '@orgus/protocol';
import { ApiError, api, apiGet, apiPost, runAction, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { NO_PROBES, readAuth, readMissions, readNotify } from './model.ts';
import type { ModuleProbes } from './model.ts';
import { CUSTOM_TASK } from './customTask.ts';

export type Phase = 'loading' | 'ready' | 'error';

interface OnboardingStore {
  phase: Phase;
  error: UiError | null;
  state: OnboardingState;
  templatesPhase: Phase;
  templatesError: UiError | null;
  templates: MissionTemplate[];
  reachPhase: Phase;
  reachError: UiError | null;
  reach: HubReach | null;
  probes: ModuleProbes;
  /** Which optional modules the hub runs, from GET /api/onboarding; null when the hub did not say. */
  modules: Record<OnboardingModule, boolean> | null;
}

export const useOnboarding = create<OnboardingStore>()(() => ({
  phase: 'loading',
  error: null,
  state: emptyOnboardingState(),
  templatesPhase: 'loading',
  templatesError: null,
  templates: [],
  reachPhase: 'loading',
  reachError: null,
  reach: null,
  probes: NO_PROBES,
  modules: null,
}));

const isMock = () => useOrgus.getState().mode === 'mock';
const notFound = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 405);
/**
 * The hub has no such route at all (it does not run that module): the hub's own 404 or 405,
 * which carries no code. A module's answer about the thing asked for (a deleted template, an
 * unknown machine or project) carries a code, and must reach the person, never a fallback.
 */
export const routeMissing = (e: unknown): boolean => notFound(e) && !(e as ApiError).ui.code;
const set = (p: Partial<OnboardingStore>) => useOnboarding.setState(p);

// ---------------------------------------------------------------------------
// Onboarding state
// ---------------------------------------------------------------------------

/** Demo mode: a hub that has walked through nothing yet, so every step can be seen. */
let mockState: OnboardingState = emptyOnboardingState();

export async function loadOnboarding(): Promise<void> {
  if (isMock()) {
    set({ phase: 'ready', error: null, state: mockState });
    return;
  }
  try {
    const { modules, ...state } = await apiGet<OnboardingResponse>('/api/onboarding');
    set({ phase: 'ready', error: null, state, modules: modules ?? null });
  } catch (e) {
    // A hub without the module: behave as if nothing was ever recorded.
    if (notFound(e)) set({ phase: 'ready', error: null, state: emptyOnboardingState() });
    else set({ phase: useOnboarding.getState().phase === 'ready' ? 'ready' : 'error', error: toUiError(e) });
  }
}

/**
 * Apply a change. The store updates at once and rolls back if the hub refuses. A hub without
 * the onboarding module keeps the change in this tab only, so the page still works.
 */
export async function updateOnboarding(u: OnboardingUpdate): Promise<OnboardingState> {
  const prev = useOnboarding.getState().state;
  const optimistic = applyOnboardingUpdate(prev, u, 'you', Date.now());
  set({ state: optimistic });
  if (isMock()) {
    mockState = optimistic;
    return optimistic;
  }
  try {
    const state = await apiPost<OnboardingState>('/api/onboarding', u);
    set({ state });
    return state;
  } catch (e) {
    if (notFound(e)) return optimistic;
    set({ state: prev });
    throw e;
  }
}

const AUTO_OPENED_KEY = 'orgus.onboarding.autoOpened';

/** Getting started opens by itself at most once per tab, whatever the hub remembers. */
export function claimAutoOpen(): boolean {
  try {
    if (sessionStorage.getItem(AUTO_OPENED_KEY)) return false;
    sessionStorage.setItem(AUTO_OPENED_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

let mockCustom: MissionTemplate[] = [];

/** False only when the hub said it does not run that module; unknown counts as maybe. */
const has = (m: OnboardingModule): boolean => useOnboarding.getState().modules?.[m] !== false;

/**
 * May a start fall back to the next way after this error from module m? Only when the module
 * turned out not to be there: never when the hub said it runs it (then every error is the
 * module's answer), and otherwise only on the hub's own route-level 404.
 */
const moduleAbsent = (e: unknown, m: OnboardingModule): boolean => useOnboarding.getState().modules?.[m] !== true && routeMissing(e);

export async function loadTemplates(): Promise<void> {
  if (isMock() || !has('templates')) {
    set({ templatesPhase: 'ready', templatesError: null, templates: [...BUILTIN_TEMPLATES, ...mockCustom] });
    return;
  }
  try {
    const r = await apiGet<TemplatesState>('/api/templates');
    set({ templatesPhase: 'ready', templatesError: null, templates: r.templates });
  } catch (e) {
    // Without the module the four built-ins still work: they start through session.start.
    if (notFound(e)) set({ templatesPhase: 'ready', templatesError: null, templates: [...BUILTIN_TEMPLATES] });
    else set({ templatesPhase: useOnboarding.getState().templatesPhase === 'ready' ? 'ready' : 'error', templatesError: toUiError(e) });
  }
}

export async function saveTemplate(input: TemplateInput, id?: string): Promise<MissionTemplate> {
  if (isMock()) {
    const now = Date.now();
    const prev = id ? mockCustom.find((t) => t.id === id) : undefined;
    const t: MissionTemplate = {
      id: prev?.id ?? `tpl_demo${now}`,
      title: input.title,
      summary: input.summary ?? '',
      icon: input.icon ?? 'sparkles',
      missionTitle: input.missionTitle !== undefined ? input.missionTitle.trim() : prev && prev.missionTitle !== prev.title ? prev.missionTitle : '',
      goal: input.goal,
      prompt: input.prompt ?? '',
      fields: input.fields ?? [],
      mode: input.mode ?? 'headless',
      branch: input.branch ?? null,
      tags: input.tags ?? [],
      builtIn: false,
      createdBy: 'you',
      createdAt: prev?.createdAt ?? now,
      updatedAt: now,
    };
    mockCustom = prev ? mockCustom.map((x) => (x.id === t.id ? t : x)) : [...mockCustom, t];
    await loadTemplates();
    return t;
  }
  const r = id
    ? await api<{ template: MissionTemplate }>(`/api/templates/${encodeURIComponent(id)}`, { method: 'PATCH', body: input })
    : await apiPost<{ template: MissionTemplate }>('/api/templates', input);
  await loadTemplates();
  return r.template;
}

export async function deleteTemplate(id: string): Promise<void> {
  if (isMock()) mockCustom = mockCustom.filter((t) => t.id !== id);
  else await api(`/api/templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
  await loadTemplates();
}

export interface StartResult {
  /** Where to look next: the mission page when the missions module ran it, else Sessions. */
  href: string;
  title: string;
  via: 'missions' | 'templates' | 'session';
  /** The machine it runs on. */
  machineId: string;
  /** The mission, when the missions module ran it. */
  missionId?: string;
  message?: string;
}

/**
 * Start a mission from a template. With the missions module: create the mission, then start
 * it (it shows on Missions with its sessions). Without it: the templates module runs
 * session.start with the rendered prompt; without that too, the page runs session.start itself.
 */
export async function startFromTemplate(t: MissionTemplate, req: TemplateStartRequest & { projectId: string }): Promise<StartResult> {
  const custom = t.id === CUSTOM_TASK.id;
  if (custom && (typeof req.values?.description !== 'string' || !req.values.description.trim())) {
    throw new ApiError({ what: 'A task description is needed.', why: 'The description is empty.', next: 'Enter a description, then start the mission.' });
  }
  const rendered = renderTemplate(t, req.values ?? {});
  const mode = req.mode ?? t.mode;
  if (isMock()) {
    await runAction(req.machineId, 'session.start', {
      ...(req.checkoutId ? { checkoutId: req.checkoutId } : { cwd: req.cwd! }),
      prompt: templatePrompt(rendered),
      mode,
      ...(req.newWorktree ? { newWorktree: req.newWorktree } : {}),
    });
    if (!useOnboarding.getState().state.marks.mission) await updateOnboarding({ op: 'mark', step: 'mission', how: 'done' });
    return { href: '/sessions', title: rendered.title, via: 'session', machineId: req.machineId };
  }

  // 1. The missions module, when the hub has it.
  let mission: { id: string } | null = null;
  if (has('missions')) {
    try {
      const r = await apiPost<{ mission?: { id: string } } & { id?: string }>('/api/missions', { projectId: req.projectId, title: rendered.title, goal: rendered.goal });
      mission = r.mission ?? (typeof r.id === 'string' ? { id: r.id } : null);
    } catch (e) {
      if (!moduleAbsent(e, 'missions')) throw e;
    }
  }
  if (mission) {
    // The mission exists now: if starting fails, say where it is rather than inviting a duplicate.
    const notStarted = (why: string) =>
      new ApiError({ what: 'The mission was created but did not start.', why, next: `Open "${rendered.title}" on Missions and start it from there.` });
    let r: { ok?: boolean; message?: string };
    try {
      r = await apiPost<{ ok?: boolean; message?: string }>(`/api/missions/${encodeURIComponent(mission.id)}/start`, {
        machineId: req.machineId,
        ...(req.checkoutId ? { checkoutId: req.checkoutId } : { cwd: req.cwd }),
        mode,
        ...(req.model ? { model: req.model } : {}),
        note: rendered.instructions,
        ...(req.newWorktree ? { newWorktree: req.newWorktree } : {}),
      });
    } catch (e) {
      throw notStarted(toUiError(e).why);
    }
    if (r.ok === false) throw notStarted(r.message ?? 'The machine did not say why.');
    await updateOnboarding({ op: 'mark', step: 'mission', how: 'done' }).catch(() => undefined);
    return { href: `/missions/${encodeURIComponent(mission.id)}`, title: rendered.title, via: 'missions', machineId: req.machineId, missionId: mission.id, ...(r.message ? { message: r.message } : {}) };
  }

  // 2. The templates module.
  if (!custom && has('templates')) {
    try {
      const r = await apiPost<TemplateStartResponse>(`/api/templates/${encodeURIComponent(t.id)}/start`, req);
      if (!r.ok) throw new ApiError({ what: 'The mission did not start.', why: r.message ?? 'The machine did not say why.', next: 'Check the machine, then try again.' });
      // The hub marked the step and broadcast it; show it here now rather than on the next poll.
      await loadOnboarding();
      return { href: '/sessions', title: r.rendered.title, via: 'templates', machineId: req.machineId, ...(r.message ? { message: r.message } : {}) };
    } catch (e) {
      if (!moduleAbsent(e, 'templates')) throw e;
    }
  }

  // 3. A hub with neither module: a plain session with the same prompt.
  const res = await runAction(req.machineId, 'session.start', {
    ...(req.checkoutId ? { checkoutId: req.checkoutId } : { cwd: req.cwd! }),
    prompt: templatePrompt(rendered),
    mode,
    ...(req.model ? { model: req.model } : {}),
    ...(req.newWorktree ? { newWorktree: req.newWorktree } : {}),
  });
  await updateOnboarding({ op: 'mark', step: 'mission', how: 'done' }).catch(() => undefined);
  return { href: '/sessions', title: rendered.title, via: 'session', machineId: req.machineId, ...(res.message ? { message: res.message } : {}) };
}

// ---------------------------------------------------------------------------
// Reach and module probes
// ---------------------------------------------------------------------------

const MOCK_REACH = (): HubReach => ({
  hostname: 'studio',
  port: 4600,
  loopbackOnly: false,
  otherMachines: true,
  checkedAt: Date.now(),
  addresses: [
    { url: 'http://100.101.24.7:4600', kind: 'tailscale', https: false, reachable: true, iface: 'Tailscale' },
    { url: 'http://192.168.1.20:4600', kind: 'lan', https: false, reachable: true, iface: 'Wi-Fi' },
    { url: 'http://127.0.0.1:4600', kind: 'loopback', https: false, reachable: true },
  ],
});

export async function loadReach(fresh = false): Promise<void> {
  if (useOnboarding.getState().reachPhase !== 'ready') set({ reachPhase: 'loading' });
  if (isMock()) {
    set({ reachPhase: 'ready', reachError: null, reach: MOCK_REACH() });
    return;
  }
  try {
    const reach = await apiGet<HubReach>(`/api/onboarding/reach${fresh ? '?fresh=1' : ''}`);
    set({ reachPhase: 'ready', reachError: null, reach });
  } catch (e) {
    set({ reachPhase: 'error', reachError: toUiError(e) });
  }
}

async function optional<T>(path: string, module: OnboardingModule): Promise<{ ok: true; data: T } | { ok: false; missing: boolean }> {
  // Ask only modules the hub runs: a missing one would answer 404, which browsers log as an error.
  if (!has(module)) return { ok: false, missing: true };
  try {
    return { ok: true, data: await apiGet<T>(path) };
  } catch (e) {
    return { ok: false, missing: notFound(e) };
  }
}

/** Ask the optional modules what they know. A missing module is recorded as not installed. */
export async function loadProbes(): Promise<void> {
  if (isMock()) {
    set({
      probes: {
        auth: { installed: true, githubConfigured: false, me: { id: 'owner', role: 'owner', via: 'owner-link', name: null } },
        missions: { installed: false, count: 0 },
        notify: { installed: false, phones: [], channels: [], publicUrl: null },
      },
    });
    return;
  }
  const [providers, me, missions, notify] = await Promise.all([
    optional<unknown>('/api/auth/providers', 'auth'),
    optional<unknown>('/api/me', 'auth'),
    optional<unknown>('/api/missions', 'missions'),
    optional<unknown>('/api/notify', 'notify'),
  ]);
  const prev = useOnboarding.getState().probes;
  set({
    probes: {
      auth: providers.ok ? readAuth(providers.data, me.ok ? me.data : null) : providers.missing ? { installed: false, githubConfigured: false, me: null } : prev.auth,
      missions: missions.ok ? readMissions(missions.data) : missions.missing ? { installed: false, count: 0 } : prev.missions,
      notify: notify.ok ? readNotify(notify.data) : notify.missing ? { installed: false, phones: [], channels: [], publicUrl: null } : prev.notify,
    },
  });
}

/** Normalised phone address the person saved, or null. */
export const savedPhoneUrl = (s: OnboardingState): string | null => (s.phoneUrl ? normalizeUrl(s.phoneUrl) : null);

/**
 * Keep onboarding data fresh while a view that needs it is mounted: load once, again when the
 * window regains focus, and (for the parts that change by themselves) on an interval.
 */
export function useOnboardingData(opts: { templates?: boolean; reach?: boolean; pollMs?: number } = {}): void {
  const mode = useOrgus((s) => s.mode);
  const { templates = false, reach = false, pollMs = 0 } = opts;
  useEffect(() => {
    // Onboarding first: its answer says which other modules are worth asking.
    const refresh = () =>
      void loadOnboarding().then(() => {
        void loadProbes();
        if (templates) void loadTemplates();
      });
    refresh();
    if (reach) void loadReach();
    const onFocus = () => {
      refresh();
      if (reach) void loadReach();
    };
    window.addEventListener('focus', onFocus);
    const timer = pollMs > 0 ? setInterval(refresh, pollMs) : null;
    return () => {
      window.removeEventListener('focus', onFocus);
      if (timer) clearInterval(timer);
    };
  }, [mode, templates, reach, pollMs]);
}
